import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { CartProvider } from "@/cart/CartContext";
import { AdminLayout } from "@/components/AdminLayout";
import { Layout } from "@/components/Layout";
import { RequireAdmin } from "@/components/RequireAdmin";
import { RequireAuth } from "@/components/RequireAuth";
import { ApplicationPage } from "@/pages/ApplicationPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { CatalogPage } from "@/pages/CatalogPage";
import { CartPage } from "@/pages/CartPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { OrdersPage } from "@/pages/OrdersPage";
import { OrderDetailPage } from "@/pages/OrderDetailPage";
import { AccountPage } from "@/pages/AccountPage";
import { TermsAcceptancePage } from "@/pages/TermsAcceptancePage";
import { AdminApplicationsPage } from "@/pages/admin/AdminApplicationsPage";
import { AdminDemandLogPage } from "@/pages/admin/AdminDemandLogPage";
import { AdminEmailLogsPage } from "@/pages/admin/AdminEmailLogsPage";
import { AdminOrdersPage } from "@/pages/admin/AdminOrdersPage";
import { AdminTenantsPage } from "@/pages/admin/AdminTenantsPage";
import { AdminInventoryPage } from "@/pages/admin/AdminInventoryPage";
import { AdminSettingsPage } from "@/pages/admin/AdminSettingsPage";
import { ShopeeConnectionsPage } from "@/pages/shopee/ShopeeConnectionsPage";
import { ShopeeConfirmPage } from "@/pages/shopee/ShopeeConfirmPage";
import { ShopeeSyncSettingsPage } from "@/pages/shopee/ShopeeSyncSettingsPage";

function RoleBasedRedirect() {
  const { status, user } = useAuth();
  if (status === "initializing") {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        読み込み中...
      </div>
    );
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }
  if (user && user.terms_accepted === false) {
    return <Navigate to="/terms" replace />;
  }
  return <Navigate to={user?.role === "admin" ? "/admin/orders" : "/catalog"} replace />;
}

/**
 * /terms のガード。
 * - 未ログイン: /login へ
 * - 既に同意済み: 役割に応じて /catalog or /admin/orders へ
 * - 未同意: TermsAcceptancePage を表示
 */
function TermsGate() {
  const { status, user } = useAuth();
  if (status === "initializing") {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        読み込み中...
      </div>
    );
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }
  if (user && user.terms_accepted === true) {
    return <Navigate to={user.role === "admin" ? "/admin/orders" : "/catalog"} replace />;
  }
  return <TermsAcceptancePage />;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* 公開: 新規利用申込フォーム（未ログインでアクセス可） */}
            <Route path="/apply" element={<ApplicationPage />} />

            {/* 利用規約同意（ログイン済みだが未同意時の強制画面） */}
            <Route path="/terms" element={<TermsGate />} />

            {/* 顧客画面 */}
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:id" element={<OrderDetailPage />} />
              <Route path="/account" element={<AccountPage />} />
              {/* Shopee 連携 (Phase C #13 / #14) */}
              <Route
                path="/account/shopee/connections"
                element={<ShopeeConnectionsPage />}
              />
              <Route
                path="/account/shopee/sync-settings"
                element={<ShopeeSyncSettingsPage />}
              />
              <Route path="/shopee/confirm" element={<ShopeeConfirmPage />} />
            </Route>

            {/* 管理者画面 */}
            <Route
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route path="/admin" element={<Navigate to="/admin/orders" replace />} />
              <Route path="/admin/orders" element={<AdminOrdersPage />} />
              <Route path="/admin/tenants" element={<AdminTenantsPage />} />
              <Route path="/admin/inventory" element={<AdminInventoryPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="/admin/applications" element={<AdminApplicationsPage />} />
              <Route path="/admin/demand-log" element={<AdminDemandLogPage />} />
              <Route path="/admin/email-logs" element={<AdminEmailLogsPage />} />
            </Route>

            {/* ルートと未知のパス: ログイン状況＋role で自動振り分け */}
            <Route path="/" element={<RoleBasedRedirect />} />
            <Route path="*" element={<RoleBasedRedirect />} />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
